import { useActionHandler, useTranslation } from "@/hooks";
import { useBookmarksContext } from "@/stores/bookmark";
import { useUIStore } from "@/stores/ui";
import { hasGeographicData } from "@/utils/data/hasGeographicData";
import { useProcessedData } from "@/stores/data-processing";
import {
  useActiveDataflow,
  useActiveDataflowId,
  useDataflowActions,
  useDataflowStore,
} from "@/stores/dataflow";
import { useView } from "@/stores/ui";
import { useUserFiles } from "@/stores/user-file";
import type { Bookmark } from "@/types/europafacts/Bookmark";
import type {
  DataInputTransformer,
  UserFileTransformer,
} from "@/types/workspace";
import type { UserFile } from "@/types/workspace/UserFile";
import {
  ActionIcon,
  Alert,
  Button,
  Card,
  Flex,
  Group,
  Menu,
  Text,
} from "@mantine/core";
import {
  IconCancel,
  IconDotsVertical,
  IconFile,
  IconFileAlert,
  IconFileCode,
  IconFileSpreadsheet,
  IconFileText,
  IconRefresh,
  type Icon,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";

export function WorkspaceSidebarFlowInput({
  data,
  isNewlyAdded = false,
  isRemoving = false,
}: {
  data?: DataInputTransformer | UserFileTransformer;
  isNewlyAdded?: boolean;
  isRemoving?: boolean;
}) {
  const { getBookmarkFromTableId, loading } = useBookmarksContext();
  const userFiles = useUserFiles();
  const { currentLanguage } = useTranslation();
  const actionHandler = useActionHandler();
  const dataflowData = useActiveDataflow();
  const dataflowId = useDataflowStore((state) => state.activeDataflowId);
  const activeDataflowId = useActiveDataflowId();
  const { removeTransformer } = useDataflowActions();
  const [dataset, setDataset] = useState<Bookmark | undefined>(undefined);
  const [userFile, setUserFile] = useState<UserFile | undefined>(undefined);
  const view = useView();
  const processedData = useProcessedData(activeDataflowId || "");
  const openSidebar = useUIStore((state) => state.openSidebar);

  useEffect(() => {
    if (!data) return;

    if (data.type === "data_input" && data.db_table_id && !loading) {
      const d = getBookmarkFromTableId(data.db_table_id);
      setDataset(d);
      setUserFile(undefined);
    } else if (data.type === "user_file") {
      const file = userFiles.find((f) => f.id === data.file_id);
      setUserFile(file);
      setDataset(undefined);
    }
  }, [data, loading, getBookmarkFromTableId, userFiles]);

  const handleRemoveDataInput = (): void => {
    if (!dataflowData?.flow_definition_json) return;
    const dataInputIndex = dataflowData.flow_definition_json.findIndex(
      (item) => item.type === "data_input" || item.type === "user_file"
    );
    if (dataInputIndex !== -1) {
      if (dataflowId) {
        removeTransformer(dataflowId, dataInputIndex);
      }
    }
  };

  if (
    !data ||
    (data.type === "data_input" && !data.db_table_id) ||
    (data.type === "user_file" && !data.file_id) ||
    (!dataset && !userFile)
  ) {
    return (
      <Card p={0} shadow="0" withBorder>
        <Flex direction="column" pb={8}>
          <Flex justify="space-between" align="center">
            <Flex p="sm" gap={8}>
              <Group gap={8}>
                <IconFileAlert
                  color="var(--mantine-color-gray-9)"
                  size={22}
                  stroke={1}
                />
                <Text c="gray.9" fz={14} fw={500}>
                  No dataset
                </Text>
              </Group>
            </Flex>
          </Flex>
          <Flex px={8} gap={8}>
            <Button
              fullWidth
              size="xs"
              onClick={() => {
                // Open datasets sidebar with callback to reopen flow sidebar
                if (dataflowId) {
                  openSidebar(dataflowId, "discover", () => {
                    openSidebar(dataflowId, "flow");
                  });
                } else {
                  actionHandler("datasets");
                }
              }}
            >
              Find dataset
            </Button>
          </Flex>
        </Flex>
      </Card>
    );
  }

  const getFileIcon = (mimeType?: string): Icon => {
    if (mimeType?.includes("csv")) return IconFileText;
    if (mimeType?.includes("excel") || mimeType?.includes("spreadsheet"))
      return IconFileSpreadsheet;
    if (mimeType?.includes("json")) return IconFileCode;
    return IconFile;
  };

  const FileIcon = getFileIcon(userFile?.type);

  // Check if map view is active and data doesn't have lat/lon or geometry
  const isMapView = view === "map";
  const hasLatLonOrGeometry = processedData?.metadata?.columns
    ? hasGeographicData(
        processedData.data,
        processedData.metadata.columns
      ).hasGeographicData
    : false;

  const showAlert =
    isMapView &&
    processedData &&
    processedData.data &&
    processedData.data.length > 0 &&
    !hasLatLonOrGeometry;

  const animationClass = isRemoving
    ? "flow-item-exit"
    : isNewlyAdded
    ? "flow-item-enter"
    : "";

  return (
    <Flex direction="column" gap={8}>
      <Card
        p={0}
        w="100%"
        bg="white"
        shadow="0"
        withBorder
        className={animationClass}
      >
        <Flex direction="column">
          <Flex justify="space-between" align="center" w="100%">
            <Flex gap={8} p="sm" align="center" flex={1} miw={0}>
              {userFile ? (
                <>
                  <FileIcon size={22} stroke={1} style={{ flexShrink: 0 }} />
                  <Text fz={14} truncate fw={500} flex={1} miw={0}>
                    {userFile.name}
                  </Text>
                </>
              ) : (
                <>
                  <IconFile size={22} stroke={1} style={{ flexShrink: 0 }} />
                  <Text fz={14} fw={500} truncate flex={1} miw={0}>
                    {dataset?.db_table.table_name_translation[currentLanguage]}
                  </Text>
                </>
              )}
            </Flex>
            <Flex px={6} style={{ flexShrink: 0 }}>
              <Menu width={200} position="left-start">
                <Menu.Target>
                  <ActionIcon size="lg" variant="subtle" color="gray.6">
                    <IconDotsVertical size={16} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>Dataset</Menu.Label>
                  <Menu.Item
                    leftSection={<IconRefresh size={16} stroke={1.5} />}
                    onClick={() => {
                      // Open datasets sidebar with callback to reopen flow sidebar
                      if (dataflowId) {
                        openSidebar(dataflowId, "discover", () => {
                          openSidebar(dataflowId, "flow");
                        });
                      } else {
                        actionHandler("datasets");
                      }
                    }}
                  >
                    Change
                  </Menu.Item>
                  <Menu.Item
                    c="red"
                    color="red"
                    leftSection={<IconCancel size={16} stroke={1.5} />}
                    onClick={handleRemoveDataInput}
                  >
                    Remove
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Flex>
          </Flex>
          {showAlert && (
            <Alert color="yellow" p={8}>
              <Flex justify="space-between" align="center" gap={8}>
                <Text px={4} c="yellow" fz={13} lh={1.4} fw={600}>
                  No location data found.
                </Text>
                <Button
                  color="yellow"
                  size="xs"
                  onClick={() => actionHandler("geocode")}
                >
                  Geocode
                </Button>
              </Flex>
            </Alert>
          )}
        </Flex>
      </Card>
    </Flex>
  );
}
